import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Briefcase, Mail, Lock, User, ArrowLeft, Users, FileText, ExternalLink, Phone, ShieldCheck, Loader2, ArrowRight, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { COUNTRY_CALLING_CODES } from '../data/countryCodes';
import { SEO } from '../components/SEO';
import { getMainCategories, getSubCategories, getSubSubCategories, getTrainingTypes } from '../data/globalData';

// OTP Input Component
function OTPInput({ length = 6, value, onChange }) {
  const inputRefs = useRef([]);

  const handleChange = (index, digit) => {
    if (!/^\d*$/.test(digit)) return;
    const newVal = value.split('');
    newVal[index] = digit;
    const updated = newVal.join('').slice(0, length);
    onChange(updated);
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    const nextIndex = Math.min(pasted.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
          data-testid={`otp-digit-${i}`}
        />
      ))}
    </div>
  );
}

// Countdown Timer
function CountdownTimer({ seconds, onExpire }) {
  const [timeLeft, setTimeLeft] = useState(seconds);

  useEffect(() => {
    setTimeLeft(seconds);
  }, [seconds]);

  useEffect(() => {
    if (timeLeft <= 0) { onExpire(); return; }
    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, onExpire]);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  return (
    <span className={`font-mono text-sm ${timeLeft <= 30 ? 'text-red-500' : 'text-slate-500'}`}>
      {mins}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(searchParams.get('type') || '');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Steps: 'select_type' | 'form' | 'verify_email' | 'creating'
  const [step, setStep] = useState(searchParams.get('type') ? 'form' : 'select_type');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    phoneCountryCode: '+91',
    designation: '',
    experience_years: '',
    primary_training_area: '',
    preferred_timing: ''
  });

  // 3-tier training selection for registration
  const [regMainCat, setRegMainCat] = useState('');
  const [regSubCat, setRegSubCat] = useState('');
  const [regSubSubCat, setRegSubSubCat] = useState('');

  // OTP state
  const [emailOtp, setEmailOtp] = useState('');
  const [emailMockOtp, setEmailMockOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendKey, setResendKey] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const handleSelectType = (type) => {
    setUserType(type);
    setStep('form');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!formData.phone) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!acceptedTerms) {
      toast.error('Please accept the Terms & Conditions');
      return;
    }

    // Send email OTP
    await sendEmailOTP();
  };

  const sendEmailOTP = async () => {
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API}/otp/send`, { recipient: formData.email, type: 'email' });
      if (res.data.success) {
        setEmailMockOtp(res.data.mock_otp || '');
        setStep('verify_email');
        if (res.data.email_delivered) {
          toast.success(`Verification code sent to ${formData.email}`, {
            description: 'Please check your inbox (and spam folder)',
            duration: 8000
          });
        } else {
          toast.success(`OTP sent to ${formData.email}`, {
            description: `[Demo Mode] Your OTP is: ${res.data.mock_otp}`,
            duration: 15000
          });
        }
        setCanResend(false);
        setResendKey(prev => prev + 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyEmailAndCreateAccount = async () => {
    if (emailOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API}/otp/verify`, {
        recipient: formData.email,
        otp: emailOtp,
        type: 'email'
      });
      if (res.data.success) {
        toast.success('Email verified!');
        // Create account directly
        await createAccount();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtpLoading(false);
    }
  };

  const createAccount = async () => {
    setStep('creating');
    setLoading(true);
    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        user_type: userType,
        phone: formData.phone,
        phone_country_code: formData.phoneCountryCode,
        ...(userType === 'trainer' && {
          designation: formData.designation,
          experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
          primary_training_area: formData.primary_training_area,
          preferred_timing: formData.preferred_timing
        })
      });
      toast.success('Account created successfully!');
      navigate(user.user_type === 'trainer' ? '/trainer/dashboard' : '/corporate/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator
  const steps = [
    { id: 'form', label: 'Details' },
    { id: 'verify_email', label: 'Verify Email' },
  ];
  const currentStepIndex = steps.findIndex(s => s.id === step);

  return (
    <div className="min-h-screen bg-white flex" data-testid="register-page">
      <SEO
        title="Create Account"
        description="Join Training Solutions - Register as a trainer to offer corporate training services or as a company to find expert trainers. Free to get started."
        path="/register"
      />
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24 py-12">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <span className="font-outfit font-bold text-2xl text-slate-900">Training Solutions</span>
            </div>
            <h2 className="font-outfit text-3xl font-bold text-slate-900 mt-6">Create your account</h2>
            <p className="mt-2 text-slate-500">Join the leading corporate training marketplace</p>
          </div>

          {/* Progress Steps */}
          {step !== 'select_type' && step !== 'creating' && (
            <div className="flex items-center gap-2 mb-6" data-testid="progress-steps">
              {steps.map((s, i) => (
                <React.Fragment key={s.id}>
                  <div className={`flex items-center gap-1.5 ${i <= currentStepIndex ? 'text-blue-600' : 'text-slate-300'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      i < currentStepIndex ? 'bg-green-500 text-white' :
                      i === currentStepIndex ? 'bg-blue-600 text-white' :
                      'bg-slate-200 text-slate-400'
                    }`}>
                      {i < currentStepIndex ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 ${i < currentStepIndex ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Step: Select Account Type */}
          {step === 'select_type' && (
            <div className="space-y-4" data-testid="account-type-selection">
              <p className="text-sm font-medium text-slate-700 mb-4">I want to join as:</p>
              <button
                onClick={() => handleSelectType('trainer')}
                className="w-full p-6 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 transition-all group text-left"
                data-testid="select-trainer-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-lg text-slate-900">Trainer</h3>
                    <p className="text-slate-500 text-sm mt-1">Offer training services and get booked by companies</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleSelectType('corporate')}
                className="w-full p-6 border-2 border-slate-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group text-left"
                data-testid="select-corporate-btn"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Users className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-lg text-slate-900">Company</h3>
                    <p className="text-slate-500 text-sm mt-1">Post training requirements and hire expert trainers</p>
                  </div>
                </div>
              </button>
              <p className="mt-6 text-center text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="login-link">Sign in</Link>
              </p>
            </div>
          )}

          {/* Step: Registration Form */}
          {step === 'form' && (
            <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="register-form">
              {/* Selected Type */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${userType === 'trainer' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                    {userType === 'trainer' ? <User className="w-4 h-4 text-blue-600" /> : <Users className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <span className="text-sm font-medium text-slate-700">
                    {userType === 'trainer' ? 'Trainer Account' : 'Company Account'}
                  </span>
                </div>
                <button type="button" onClick={() => { setUserType(''); setStep('select_type'); }} className="text-sm text-slate-500 hover:text-slate-700">
                  Change
                </button>
              </div>

              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  {userType === 'trainer' ? 'Full Name' : 'Company Name'}
                </Label>
                <div className="mt-1.5 relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input id="name" type="text" placeholder={userType === 'trainer' ? 'John Doe' : 'Acme Corp'}
                    value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-12 border-slate-200" data-testid="name-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input id="email" type="email" placeholder="you@company.com"
                    value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12 border-slate-200" data-testid="email-input" />
                </div>
              </div>

              {/* Phone Number with Country Code */}
              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number</Label>
                <div className="mt-1.5 flex gap-2">
                  <select
                    value={formData.phoneCountryCode}
                    onChange={(e) => setFormData({ ...formData, phoneCountryCode: e.target.value })}
                    className="w-28 h-12 px-2 rounded-lg border border-slate-200 text-slate-700 text-sm focus:ring-2 focus:ring-blue-500"
                    data-testid="phone-country-code"
                  >
                    {COUNTRY_CALLING_CODES.map((c) => (
                      <option key={`${c.iso}-${c.code}`} value={c.code}>
                        {c.code} {c.iso}
                      </option>
                    ))}
                  </select>
                  <div className="flex-1 relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input id="phone" type="tel" placeholder="9876543210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                      className="pl-10 h-12 border-slate-200" data-testid="phone-input" />
                  </div>
                </div>
              </div>

              {/* Trainer-specific extended fields */}
              {userType === 'trainer' && (
                <div className="space-y-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                  <p className="text-sm font-semibold text-blue-700">Professional Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-sm">Designation / Title</Label>
                      <Input
                        placeholder="e.g. Senior Trainer"
                        value={formData.designation}
                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        className="mt-1 h-10 border-slate-200 text-sm"
                        data-testid="designation-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-sm">Experience (Years)</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 5"
                        min={0}
                        value={formData.experience_years}
                        onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                        className="mt-1 h-10 border-slate-200 text-sm"
                        data-testid="experience-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-700 text-sm">Primary Training Area</Label>
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      <select
                        value={regMainCat}
                        onChange={(e) => { setRegMainCat(e.target.value); setRegSubCat(''); setRegSubSubCat(''); setFormData(f => ({...f, primary_training_area: ''})); }}
                        className="h-9 px-2 text-xs rounded border border-slate-200"
                        data-testid="reg-main-cat"
                      >
                        <option value="">Category</option>
                        {getMainCategories().map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select
                        value={regSubCat}
                        onChange={(e) => { setRegSubCat(e.target.value); setRegSubSubCat(''); setFormData(f => ({...f, primary_training_area: ''})); }}
                        className="h-9 px-2 text-xs rounded border border-slate-200"
                        disabled={!regMainCat}
                        data-testid="reg-sub-cat"
                      >
                        <option value="">Sub Category</option>
                        {getSubCategories(regMainCat).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select
                        value={regSubSubCat}
                        onChange={(e) => { setRegSubSubCat(e.target.value); setFormData(f => ({...f, primary_training_area: ''})); }}
                        className="h-9 px-2 text-xs rounded border border-slate-200"
                        disabled={!regSubCat}
                        data-testid="reg-subsub-cat"
                      >
                        <option value="">Area</option>
                        {getSubSubCategories(regMainCat, regSubCat).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    {regSubSubCat && (
                      <select
                        value={formData.primary_training_area}
                        onChange={(e) => setFormData({ ...formData, primary_training_area: e.target.value })}
                        className="w-full mt-2 h-9 px-2 text-xs rounded border border-slate-200"
                        data-testid="reg-training-type"
                      >
                        <option value="">Select Training Type</option>
                        {getTrainingTypes(regMainCat, regSubCat, regSubSubCat).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                  </div>

                  <div>
                    <Label className="text-slate-700 text-sm">Preferred Timing</Label>
                    <select
                      value={formData.preferred_timing}
                      onChange={(e) => setFormData({ ...formData, preferred_timing: e.target.value })}
                      className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm"
                      data-testid="preferred-timing"
                    >
                      <option value="">Select preferred timing</option>
                      <option value="Morning (9AM - 12PM)">Morning (9AM - 12PM)</option>
                      <option value="Afternoon (12PM - 4PM)">Afternoon (12PM - 4PM)</option>
                      <option value="Evening (4PM - 8PM)">Evening (4PM - 8PM)</option>
                      <option value="Flexible">Flexible</option>
                      <option value="Weekends Only">Weekends Only</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input id="password" type="password" placeholder="Min. 6 characters"
                    value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 h-12 border-slate-200" data-testid="password-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input id="confirmPassword" type="password" placeholder="Re-enter password"
                    value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 h-12 border-slate-200" data-testid="confirm-password-input" />
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Checkbox id="terms" checked={acceptedTerms} onCheckedChange={(checked) => setAcceptedTerms(checked)}
                  className="mt-0.5" data-testid="terms-checkbox" />
                <div className="flex-1">
                  <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer">
                    I have read and agree to the{' '}
                    <Link to={`/terms?type=${userType}`} target="_blank"
                      className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1" data-testid="terms-link">
                      <FileText className="w-3 h-3" />
                      {userType === 'corporate' ? 'Corporate' : 'Trainer'} Service Agreement
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">Including payment terms, cancellation policy, and platform guidelines</p>
                </div>
              </div>

              <Button type="submit" disabled={loading || !acceptedTerms || otpLoading}
                className={`w-full h-12 text-white rounded-lg font-medium text-base ${
                  userType === 'trainer' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'
                } ${!acceptedTerms ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="continue-to-verify-btn">
                {otpLoading ? (
                  <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Sending OTP...</div>
                ) : (
                  <div className="flex items-center gap-2">Continue to Verification <ArrowRight className="w-4 h-4" /></div>
                )}
              </Button>

              <p className="text-center text-slate-500 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="login-link">Sign in</Link>
              </p>
            </form>
          )}

          {/* Step: Verify Email OTP */}
          {step === 'verify_email' && (
            <div className="space-y-6" data-testid="verify-email-step">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Verify your email</h3>
                <p className="text-slate-500 mt-2 text-sm">
                  We've sent a 6-digit code to<br />
                  <span className="font-medium text-slate-700">{formData.email}</span>
                </p>
              </div>

              {/* Mock OTP Banner - only show when OTP is mocked */}
              {emailMockOtp ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="mock-otp-banner-email">
                  <p className="text-xs text-amber-700 font-medium">Demo Mode — Your OTP is:</p>
                  <p className="text-2xl font-bold text-amber-800 tracking-widest mt-1">{emailMockOtp}</p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center" data-testid="email-sent-banner">
                  <p className="text-sm text-blue-700 font-medium">Check your inbox for the 6-digit verification code</p>
                  <p className="text-xs text-blue-500 mt-1">Don't see it? Check your spam folder</p>
                </div>
              )}

              <OTPInput length={6} value={emailOtp} onChange={setEmailOtp} />

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Expires in: <CountdownTimer key={`email-${resendKey}`} seconds={300} onExpire={() => setCanResend(true)} /></span>
                <button
                  onClick={sendEmailOTP}
                  disabled={!canResend || otpLoading}
                  className={`flex items-center gap-1 font-medium ${canResend ? 'text-blue-600 hover:text-blue-700' : 'text-slate-400 cursor-not-allowed'}`}
                  data-testid="resend-email-otp"
                >
                  <RotateCcw className="w-3 h-3" /> Resend
                </button>
              </div>

              <Button onClick={verifyEmailAndCreateAccount} disabled={emailOtp.length !== 6 || otpLoading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                data-testid="verify-email-btn">
                {otpLoading ? (
                  <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Verifying...</div>
                ) : (
                  <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Verify & Create Account</div>
                )}
              </Button>

              <button onClick={() => setStep('form')} className="w-full text-center text-sm text-slate-500 hover:text-slate-700">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to form
              </button>
            </div>
          )}

          {/* Step: Creating Account */}
          {step === 'creating' && (
            <div className="text-center py-12" data-testid="creating-account">
              <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin mb-4" />
              <h3 className="text-xl font-bold text-slate-800">Creating your account...</h3>
              <p className="text-slate-500 mt-2">Setting everything up for you</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Side */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-50 to-emerald-50 items-center justify-center p-12">
        <div className="max-w-lg text-center">
          <div className="w-64 h-64 mx-auto mb-8 bg-white rounded-2xl shadow-xl flex items-center justify-center">
            <div className="relative">
              <div className="w-40 h-40 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-full flex items-center justify-center">
                {step === 'verify_email' ? (
                  <ShieldCheck className="w-20 h-20 text-blue-600" />
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center">
                      <Briefcase className="w-8 h-8 text-white" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-4">
            {step === 'verify_email' ? 'Check Your Email' : 'Join Our Network'}
          </h3>
          <p className="text-slate-500">
            {step === 'verify_email' ? 'Enter the verification code sent to your email' :
             'Connect with thousands of trainers and companies for seamless corporate training'}
          </p>
        </div>
      </div>
    </div>
  );
}
